import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React from 'react';
import { CommerceVariant, ProductOnUser } from 'common-ts/dist/models/Product';
import { RegularSizeValue, SizeType } from 'common-ts/dist/models/Cart';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import { addItemCount, postCanAddItemCount } from '../redux/CartRedux';
import { updateCurrentScreen } from '../redux/UIOverlayNavbarRedux';
import { useAppDispatch, useAppSelector } from '../reduxhooks';
import { VariantSelectionContext } from './ProductPageContext';
import { FareastButton } from './OverlayComponentButton';
import { Mixpanel } from '../mixpanel';

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  optionSection: {
    margin: '18px 0px 6px',
  },
  optionName: {
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '0.6px',
    marginBottom: '10px',
    textTransform: 'uppercase',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '6px',
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '9px 18px 7px',
    borderRadius: '24px',
    border: '1px solid #d5d5d5',
    backgroundColor: '#f5f5f5',
    cursor: 'pointer',
    userSelect: 'none',
  },
  // Shopee-style: option values that carry a per-variant image show it as a
  // small swatch inside the chip. Values without an image stay text-only.
  chipSwatch: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    objectFit: 'cover',
    marginLeft: '-6px',
    display: 'block',
  },
  chipSelected: {
    backgroundColor: '#232323',
    borderColor: '#232323',
  },
  chipUnavailable: {
    opacity: 0.35,
  },
  chipText: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#232323',
  },
  chipTextSelected: {
    color: '#ffffff',
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    margin: '14px 0px 2px',
  },
  priceText: {
    fontSize: '18px',
    fontWeight: 600,
  },
  compareAtText: {
    fontSize: '14px',
    fontWeight: 300,
    color: '#9e9e9e',
    textDecoration: 'line-through',
  },
  stockText: {
    fontSize: '12px',
    fontWeight: 300,
    color: '#787878',
    marginBottom: '4px',
  },
  stockLowText: {
    color: '#c62828',
    fontWeight: 500,
  },
}));

function formatRupiah(value: number): string {
  return 'Rp ' + value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function variantIsPurchasable(v: CommerceVariant): boolean {
  if (!v.inventoryTracked) return true;
  if (v.inventoryPolicy === 'CONTINUE') return true;
  return v.inventoryQuantity > 0;
}

// Generic option/variant selector (Shopify-style) for terbebas products.
// Covers all three product shapes: 0 options (auto-resolved Default Title
// variant), 1 option, and 2 options.
export default function PageProductDetailCommerceOptions({ productDetail, ctaLabel }: {
  productDetail: ProductOnUser,
  // Merchant-configurable button copy (BlockProductBuyBox). Only overrides
  // the idle label — the "pick an option" / "sold out" / "added" states
  // still tell the shopper what is actually going on.
  ctaLabel?: string,
}) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const commerce = productDetail.commerce;
  const catalogStatus = useAppSelector(state => state.productsList.status);

  // Selection lives on the product page (VariantSelectionContext) so the
  // price block and gallery react to it. Surfaces without the provider —
  // LinkPage sheet, preview iframe — fall back to local state and behave
  // as before.
  const variantSelection = React.useContext(VariantSelectionContext);
  const [localSelected, setLocalSelected] = React.useState<{ [optionName: string]: string }>({});
  const selected = variantSelection ? variantSelection.selected : localSelected;
  const setSelected = variantSelection ? variantSelection.setSelected : setLocalSelected;
  const [justAdded, setJustAdded] = React.useState(false);

  if (!commerce) return null;

  const options = [...commerce.options].sort((a, b) => a.position - b.position);

  // Resolve the variant for the current selection. Option-less products
  // resolve immediately to their single Default Title variant.
  const allSelected = options.every((o) => selected[o.name]);
  const resolvedVariant: CommerceVariant | undefined = !options.length
    ? commerce.variants[0]
    : allSelected
      ? commerce.variants.find((v) => {
        const values = [v.option1, v.option2, v.option3];
        return options.every((o, i) => values[i] === selected[o.name]);
      })
      : undefined;

  // A value is "available" if some purchasable variant matches it together
  // with the values already selected for the *other* options.
  const valueIsAvailable = (optionIndex: number, value: string): boolean => {
    return commerce.variants.some((v) => {
      const values = [v.option1, v.option2, v.option3];
      if (values[optionIndex] !== value) return false;
      if (!variantIsPurchasable(v)) return false;
      return options.every((o, i) => {
        if (i === optionIndex) return true;
        const sel = selected[o.name];
        return !sel || values[i] === sel;
      });
    });
  };

  // Swatch image for an option value: the imageSrc of a variant that carries
  // this value (respecting other already-selected options). null when no
  // matching variant has its own image.
  const valueImage = (optionIndex: number, value: string): string | null => {
    const match = commerce.variants.find((v) => {
      const values = [v.option1, v.option2, v.option3];
      if (values[optionIndex] !== value) return false;
      return options.every((o, i) => {
        if (i === optionIndex) return true;
        const sel = selected[o.name];
        return !sel || values[i] === sel;
      });
    });
    return match?.imageSrc ?? null;
  };

  const handleAddToCart = () => {
    if (!resolvedVariant) return;

    const cartFields = {
      productId: productDetail.productId,
      fitType: productDetail.defaultFitType,
      sizeType: SizeType.regular,
      measurementCreationDateTime: 'NOTSELECTED',
      measurementUpdateDateTime: 'NOTSELECTED',
      measurementIsBodyMeasurement: false,
      regularSize: RegularSizeValue.NOTSELECTED,
      variantId: resolvedVariant.id,
      variantTitle: resolvedVariant.title,
      unitPrice: resolvedVariant.price,
      compareAtPrice: resolvedVariant.compareAtPrice,
    };

    Mixpanel.track('cartAddItem', { 'productId': 'productId-' + productDetail.productId });

    // canAddItemDict must know this item before the addItemCount reducer
    // will accept it (resolved locally from the catalog's inventory).
    dispatch(postCanAddItemCount({ ...cartFields, amount: 1, clothingType: productDetail.clothingType }))
      .then(() => {
        dispatch(addItemCount({ ...cartFields, productDetail }));
        dispatch(updateCurrentScreen('CART'));
        setJustAdded(true);
        setTimeout(() => setJustAdded(false), 1500);
      });
  };

  const stockKnown = resolvedVariant && resolvedVariant.inventoryTracked
    && resolvedVariant.inventoryPolicy === 'DENY';
  const outOfStock = resolvedVariant !== undefined && !variantIsPurchasable(resolvedVariant);

  return (
    <Box>
      {
        options.map((option, optionIndex) => (
          <Box key={option.name} className={classes.optionSection}>
            <Typography className={classes.optionName}>
              {option.name}
              {selected[option.name] ? ': ' + selected[option.name] : ''}
            </Typography>
            <Box className={classes.chipRow}>
              {
                option.values.map((value) => {
                  const isSelected = selected[option.name] === value;
                  const available = valueIsAvailable(optionIndex, value);
                  const swatch = valueImage(optionIndex, value);
                  return (
                    <Box
                      key={value}
                      className={
                        classes.chip
                        + (isSelected ? ' ' + classes.chipSelected : '')
                        + (!available ? ' ' + classes.chipUnavailable : '')
                      }
                      onClick={() => {
                        setSelected((prev) => ({ ...prev, [option.name]: value }));
                      }}
                      id={'commerce-option-' + option.name + '-' + value}
                    >
                      {swatch && <img src={swatch} alt={value} className={classes.chipSwatch} />}
                      <Typography className={
                        classes.chipText + (isSelected ? ' ' + classes.chipTextSelected : '')
                      }>
                        {value}
                      </Typography>
                    </Box>
                  );
                })
              }
            </Box>
          </Box>
        ))
      }
      {/* {
        resolvedVariant &&
        <Box className={classes.priceRow}>
          <Typography className={classes.priceText}>
            {formatRupiah(resolvedVariant.price)}
          </Typography>
          {
            resolvedVariant.compareAtPrice !== null &&
            <Typography className={classes.compareAtText}>
              {formatRupiah(resolvedVariant.compareAtPrice)}
            </Typography>
          }
        </Box>
      } */}
      {
        resolvedVariant && stockKnown && !outOfStock && resolvedVariant.inventoryQuantity <= 10 &&
        <Typography className={classes.stockText + ' ' + classes.stockLowText}>
          Sisa {resolvedVariant.inventoryQuantity} lagi
        </Typography>
      }
      {
        outOfStock &&
        <Typography className={classes.stockText + ' ' + classes.stockLowText}>
          Stok habis untuk pilihan ini
        </Typography>
      }
      <FareastButton
        buttonMarginTopBottom='12px'
        buttonMarginLeftRight='0px'
        variant="contained"
        aria-label="add to cart"
        fullWidth
        disabled={
          catalogStatus !== APIRequestStatus.Success
          || (options.length > 0 && !allSelected)
          || (allSelected && !resolvedVariant)
          || outOfStock
        }
        onClick={handleAddToCart}
      >
        <Typography id='text-add-to-cart' style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '1px' }}>
          {
            justAdded ? 'DITAMBAHKAN ✓' :
            options.length > 0 && !allSelected ? 'PILIH ' + options.filter((o) => !selected[o.name]).map((o) => o.name.toUpperCase()).join(' & ') :
            outOfStock ? 'STOK HABIS' :
            (ctaLabel || 'TAMBAH KE KERANJANG')
          }
        </Typography>
      </FareastButton>
    </Box>
  );
}
